import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Shield } from 'lucide-react';
import { useSebEnvironment } from '@/hooks/useSebEnvironment';
import { otcSebVerify } from '@/lib/otcApi';

/**
 * Blocks MCQ / Tech Assessment UI until the backend confirms a SEB session.
 * Soft UA is UX-only; server verification is required when seb_required.
 */
export function SebGate({
  otc,
  sebRequired,
  sebVerified: initiallyVerified,
  sebConfigUrl,
  sebDownloadUrlWindows,
  sebDownloadUrlMacos,
  onVerified,
  children,
}) {
  const { checked, jsApiAvailable, uaMentionsSeb, keys } = useSebEnvironment();
  const [serverVerified, setServerVerified] = useState(Boolean(initiallyVerified));
  const [verifyError, setVerifyError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!sebRequired) {
      setServerVerified(true);
      return;
    }
    if (!checked) return;

    // Stamp alone is not enough — require live SEB JS/keys (or UA) on this client.
    const inSeb = Boolean(jsApiAvailable || keys?.configKey || uaMentionsSeb);
    if (initiallyVerified && inSeb) {
      setServerVerified(true);
      return;
    }
    if (!inSeb) {
      setServerVerified(false);
      return;
    }

    // Inside SEB but Config Key not ready yet — keep waiting (hook retries).
    if (!keys?.configKey) {
      return;
    }

    let cancelled = false;
    setVerifying(true);
    setVerifyError(null);
    otcSebVerify(otc, {
      page_url: window.location.href.split('#')[0],
      js_config_key: keys.configKey,
      js_browser_exam_key: keys.browserExamKey || undefined,
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res?.verified) {
          if (typeof onVerified === 'function') {
            await onVerified();
          }
          setServerVerified(true);
        } else {
          setVerifyError(res?.reason || 'config_key_mismatch');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const d = err?.response?.data?.detail || err?.message;
        setVerifyError(typeof d === 'string' ? d : d?.message || 'verify_failed');
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });

    return () => { cancelled = true; };
  }, [checked, sebRequired, initiallyVerified, jsApiAvailable, uaMentionsSeb, keys, otc, onVerified]);

  if (!sebRequired) return children;

  if (!checked || verifying || (jsApiAvailable && !keys?.configKey && !verifyError && !serverVerified)) {
    return (
      <Center>
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
        <h1 className="mt-3 text-xl font-semibold">Verifying secure browser session…</h1>
      </Center>
    );
  }

  if (verifyError) {
    const mismatchHint = verifyError === 'config_key_mismatch' || verifyError === 'missing_config_key';
    return (
      <Center>
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-600" />
        <h1 className="mt-3 text-xl font-semibold">Secure browser check failed</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {mismatchHint
            ? 'Please re-download the exam configuration (.seb) from your invitation and open it with Safe Exam Browser.'
            : 'We could not verify Safe Exam Browser. Contact HR if this continues.'}
        </p>
        {sebConfigUrl && (
          <Button asChild className="mt-4" variant="outline">
            <a href={sebConfigUrl} data-testid="seb-redownload-config">Download .seb config</a>
          </Button>
        )}
      </Center>
    );
  }

  // Outside SEB (including after a prior server stamp in another browser) — handoff UI.
  if (!jsApiAvailable && !uaMentionsSeb && !keys?.configKey) {
    return (
      <Center>
        <Shield className="mx-auto h-10 w-10 text-primary" />
        <h1 className="mt-3 text-xl font-semibold">Safe Exam Browser required</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This assessment must be opened inside Safe Exam Browser. Install SEB, then download
          and open the exam configuration file from your invitation email.
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
          {sebDownloadUrlWindows && (
            <Button asChild variant="outline" size="sm">
              <a href={sebDownloadUrlWindows} target="_blank" rel="noreferrer" data-testid="seb-download-windows">
                SEB for Windows
              </a>
            </Button>
          )}
          {sebDownloadUrlMacos && (
            <Button asChild variant="outline" size="sm">
              <a href={sebDownloadUrlMacos} target="_blank" rel="noreferrer" data-testid="seb-download-macos">
                SEB for macOS
              </a>
            </Button>
          )}
          {sebConfigUrl && (
            <Button asChild size="sm">
              <a href={sebConfigUrl} data-testid="seb-download-config">Download .seb config</a>
            </Button>
          )}
        </div>
      </Center>
    );
  }

  if (!serverVerified) {
    return (
      <Center>
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
        <h1 className="mt-3 text-xl font-semibold">Verifying secure browser session…</h1>
      </Center>
    );
  }

  return children;
}

function Center({ children }) {
  return (
    <div className="hero-glow min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
        <Card className="card-elevated w-full">
          <CardContent className="p-8 text-center space-y-2" data-testid="seb-gate">
            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
